import React, { useState } from 'react'
import { colors } from './fieldTheme'
import { TOUR_STATES, STATE_ORDER, tourZones, pitchState, pitchMatchesState, filterCounts, hasInventoryAndIntervention, hasComment, stateCounts, zoneProgress, zoneLabel, zoneOf, matchesQuery, pitchReason } from './fieldTours'
import { getTourView, setTourView } from './fieldApi'
import { nowSeconds } from './fieldDates'
import { ChevronLeft, ChevronRight, SearchIcon, NoResultIcon, ClearIcon } from './FieldIcons'
import { Pastille, LoadingText, Notice, LinkButton, visuallyHidden } from './FieldUI'

// Porte d'entrée du tour de terrain (canvas « Plan des hébergements », M6a ; décision 50) : en-tête fixe,
// plan par zone par défaut, liste en second choix mémorisée sur le téléphone. Seulement l'avancement du
// tour : aucune information de la vue du jour (arrivées, départs, urgences).
// state : { zone: 'all' | <zone>, filter: null | 'todo' | 'done' | 'intervention' | 'inventory', query },
// gardé par l'app pour retrouver la même vue en revenant d'une fiche.

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"

const StateMark = ({ state, size = 20 }) => {
	if (state === 'done')
		return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
	if (state === 'comment')
		return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-4.5 3.5V17H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M7.5 9h9M7.5 12.5h6" /></svg>
	if (state === 'inventory')
		return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4.5h6M9 11h6M9 15h4" /></svg>
	if (state === 'intervention')
		return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3z" /></svg>
	return <svg width={size - 2} height={size - 2} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true"><circle cx="12" cy="12" r="7" /></svg>
}

function swatch(state, size)
{
	return { width: size, height: size, borderRadius: size > 14 ? 5 : 4, flex: 'none', background: TOUR_STATES[state].background, boxShadow: state === 'todo' ? 'inset 0 0 0 1px #94A3B8' : 'none' }
}

function Tile({ pitch, state, both, commented, onPick })
{
	const look = TOUR_STATES[state]
	// Inventaire + intervention : deux triangles séparés par une diagonale « \ », rouge (inventaire) en haut à droite, jaune (intervention) en bas à gauche
	const split = both ? 'linear-gradient(to bottom left, ' + TOUR_STATES.inventory.background + ' 50%, ' + TOUR_STATES.intervention.background + ' 50%)' : null
	return (
		<button
			type="button"
			className="field-press"
			aria-label={pitch.location + ', ' + zoneLabel(zoneOf(pitch)) + ', ' + look.label + (both ? ', avec intervention' : '') + (commented ? ', avec commentaire' : '')}
			onClick={onPick}
			style={{ backgroundImage: split || 'none', backgroundColor: look.background, position: 'relative', height: 76, minWidth: 0, borderRadius: 14, border: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: look.color, fontFamily: 'inherit', cursor: 'pointer', padding: '0 4px' }}
		>
			<span style={{ fontFamily: MONO, fontSize: 19, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pitch.location}</span>
			{both
				? <React.Fragment>
					<span style={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', color: TOUR_STATES.intervention.color }}><StateMark state="intervention" /></span>
					{/* la bulle prend la place de l'icône d'inventaire, dans le coin rouge */}
					{commented
						? <span style={{ position: 'absolute', top: 8, right: 8, display: 'flex' }}><StateMark state="comment" /></span>
						: <span style={{ position: 'absolute', top: 8, right: 8, display: 'flex' }}><StateMark state="inventory" /></span>}
				</React.Fragment>
				: <React.Fragment>
					<span style={{ position: 'absolute', top: 8, right: 8, display: 'flex' }}><StateMark state={state} /></span>
					{commented && <span style={{ position: 'absolute', top: 8, left: 8, display: 'flex' }}><StateMark state="comment" /></span>}
				</React.Fragment>}
		</button>
	)
}

function Row({ pitch, state, reason, onPick })
{
	return (
		<button
			type="button"
			className="field-row"
			aria-label={pitch.location + ', ' + zoneLabel(zoneOf(pitch)) + ', ' + reason}
			onClick={onPick}
			style={{ backgroundImage: 'none', backgroundColor: colors.surface, display: 'flex', alignItems: 'stretch', gap: 14, minHeight: 80, padding: '12px 0', border: 0, borderBottom: '1px solid ' + colors.separator, width: '100%', color: colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
		>
			<span aria-hidden="true" style={{ ...swatch(state, 10), height: 'auto', borderRadius: 6 }} />
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
				<span style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
					<span style={{ fontFamily: MONO, fontSize: 19, fontWeight: 700 }}>{pitch.location}</span>
					<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{zoneLabel(zoneOf(pitch))}</span>
				</span>
				<span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>{reason}</span>
				{(pitch.location_category || '').trim() && <span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{pitch.location_category.trim()}</span>}
			</span>
			{/* Pas d'icône d'état ici : la bande de couleur, la section et le texte le disent déjà (retour d'Alexandra) */}
			<span style={{ flex: 'none', display: 'flex', alignItems: 'center' }}>
				<ChevronRight />
			</span>
		</button>
	)
}

// Icônes de la bascule Plan / Liste (demande d'Alexandra du 24/10/2026) : carte pliée, lignes
const PlanIcon = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
		<path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
		<path d="M9 4v14M15 6v14" />
	</svg>
)

const ListIcon = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" style={{ flex: 'none' }}>
		<path d="M9 6h11M9 12h11M9 18h11" />
		<path d="M4 6h1M4 12h1M4 18h1" />
	</svg>
)

const chip = (on) => ({
	backgroundImage: 'none',
	backgroundColor: on ? '#1E293B' : colors.surface,
	color: on ? '#FFFFFF' : '#1E293B',
	border: '2px solid ' + (on ? '#1E293B' : '#B9C0D4'),
	flex: 'none',
	minHeight: 48,
	padding: '0 16px',
	borderRadius: 999,
	display: 'inline-flex',
	alignItems: 'center',
	gap: 6,
	fontSize: 15,
	fontWeight: 700,
	whiteSpace: 'nowrap',
	fontFamily: 'inherit',
	cursor: 'pointer'
})

const segment = (on) => ({
	backgroundImage: 'none',
	backgroundColor: on ? colors.surface : 'transparent',
	color: on ? colors.text : colors.textSecondary,
	boxShadow: on ? '0 1px 2px rgba(11,16,51,.15)' : 'none',
	minHeight: 48,
	padding: '0 14px 0 12px',
	borderRadius: 10,
	display: 'inline-flex',
	alignItems: 'center',
	gap: 6,
	border: 0,
	fontSize: 15,
	fontWeight: 800,
	fontFamily: 'inherit',
	cursor: 'pointer'
})

export default function FieldTourPlan({ tour, pitches, loadError, onRetry, state, onState, onPick, onBack })
{
	const [view, setView] = useState(getTourView)
	const now = nowSeconds()
	const { zone, filter, query } = state
	const collapsed = state.collapsed === true // recherche, zones et filtres repliés
	const set = (patch) => onState({ ...state, ...patch })

	const changeView = (next) => {
		setView(next)
		setTourView(next)
	}

	const all = pitches || []
	const zones = tourZones(all)
	const counts = stateCounts(all, tour)
	const done = all.length - counts.todo
	const searching = query.trim() !== ''
	const zoned = searching ? all.filter(pitch => matchesQuery(pitch, query)) : (zone === 'all' ? all : all.filter(pitch => zoneOf(pitch) === zone))
	const scopeCounts = filterCounts(zoned, tour)
	const scope = filter ? zoned.filter(pitch => pitchMatchesState(tour, pitch.location, filter)) : zoned
	const inScope = new Set(scope.map(pitch => pitch.location))

	const header = (
		<div style={{ position: 'sticky', top: 0, zIndex: 2, background: colors.surface, borderBottom: '1px solid ' + colors.separator, padding: '12px 20px', paddingTop: 'max(12px, env(safe-area-inset-top))', display: 'flex', flexDirection: 'column', gap: 10 }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
				<button
					type="button"
					onClick={onBack}
					style={{ backgroundImage: 'none', backgroundColor: 'transparent', flex: 'none', minHeight: 48, display: 'flex', alignItems: 'center', gap: 2, padding: '0 8px 0 0', border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
				>
					<ChevronLeft />
					Accueil
				</button>
				<div role="group" aria-label="Affichage" style={{ flex: 'none', display: 'flex', gap: 2, padding: 3, borderRadius: 13, background: '#E8EBF5' }}>
					<button type="button" aria-pressed={view === 'plan'} onClick={() => changeView('plan')} style={segment(view === 'plan')}>
						<PlanIcon />
						Plan
					</button>
					<button type="button" aria-pressed={view === 'list'} onClick={() => changeView('list')} style={segment(view === 'list')}>
						<ListIcon />
						Liste
					</button>
				</div>
			</div>
			{/* Titre et avancement : un seul bouton qui replie / déplie recherche, zones et filtres, pour laisser plus de place à la liste */}
			<button
				type="button"
				aria-expanded={!collapsed}
				aria-controls="field-tour-tools"
				aria-label={(collapsed ? 'Afficher' : 'Masquer') + ' la recherche et les filtres'}
				onClick={() => set({ collapsed: !collapsed })}
				style={{ backgroundImage: 'none', backgroundColor: 'transparent', width: '100%', display: 'flex', flexDirection: 'column', gap: 10, padding: 0, border: 0, textAlign: 'left', fontFamily: 'inherit', color: colors.text, cursor: 'pointer' }}
			>
				<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<Pastille type="tour" size={32} illustrationSize={22} />
					<h1 style={{ flex: 1, fontSize: 24, fontWeight: 800, lineHeight: 1.15, color: colors.text, overflowWrap: 'break-word', minWidth: 0 }}>{tour.name}</h1>
					<svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.textSecondary} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 160ms ease-out' }}><path d="M6 9l6 6 6-6" /></svg>
				</span>
				<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<span style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{done + ' / ' + all.length + ' faits'}</span>
					<span role="progressbar" aria-valuemin={0} aria-valuemax={all.length} aria-valuenow={done} aria-label="Avancement du tour" style={{ flex: 1, minWidth: 40, height: 8, borderRadius: 999, background: '#E8EBF5', overflow: 'hidden', display: 'block' }}>
						<span style={{ display: 'block', height: '100%', borderRadius: 999, background: colors.primary, width: (all.length ? Math.round(done * 1000 / all.length) / 10 : 0) + '%' }} />
					</span>
				</span>
				{collapsed && (searching || filter || zone !== 'all') && <span style={{ fontSize: 14, fontWeight: 600, color: colors.textSecondary }}>Recherche ou filtre actif</span>}
			</button>
			{!collapsed && (
			<div id="field-tour-tools" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
			<div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
				<label htmlFor="field-tour-search" style={visuallyHidden}>Rechercher un numéro d'emplacement</label>
				<span aria-hidden="true" style={{ position: 'absolute', left: 14, display: 'flex' }}><SearchIcon size={22} color={colors.textSecondary} /></span>
				<input
					id="field-tour-search"
					type="search"
					className="browser-default"
					value={query}
					autoComplete="off"
					placeholder="Rechercher un n° (ex : J069)"
					onChange={(e) => set({ query: e.target.value })}
					style={{ width: '100%', minHeight: 48, margin: 0, padding: query ? '0 52px 0 46px' : '0 16px 0 46px', borderRadius: 12, border: 0, background: '#F1F5F9', fontSize: 17, fontFamily: 'inherit', color: colors.text, boxSizing: 'border-box' }}
				/>
				{query && (
					<button type="button" aria-label="Effacer la recherche" onClick={() => set({ query: '' })} style={{ backgroundImage: 'none', backgroundColor: 'transparent', position: 'absolute', right: 0, width: 48, height: 48, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textSecondary, cursor: 'pointer' }}>
						<ClearIcon />
					</button>
				)}
			</div>
			<div role="group" aria-label="Zones" style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '0 -20px', padding: '0 20px 2px' }}>
				{[{ zone: 'all', name: 'Toutes', pitches: all }].concat(zones.map(group => ({ zone: group.zone, name: group.zone || 'Sans zone', pitches: group.pitches }))).map(item => {
					const on = !searching && zone === item.zone
					const progress = zoneProgress(item.pitches, tour)
					return (
						<button key={item.zone || '-'} type="button" aria-pressed={on} onClick={() => set({ zone: item.zone, query: '' })} style={chip(on)}>
							{item.name}
							<span style={{ fontWeight: 500 }}>{progress.done + '/' + progress.total}</span>
						</button>
					)
				})}
			</div>
			<div role="group" aria-label="Filtrer par avancement" style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '0 -20px', padding: '0 20px 2px' }}>
				{STATE_ORDER.map(key => {
					const on = filter === key
					const n = scopeCounts[key]
					return (
						<button
							key={key}
							type="button"
							aria-pressed={on}
							onClick={() => set({ filter: on ? null : key })}
							style={{ backgroundImage: 'none', backgroundColor: on ? '#1E293B' : colors.surface, color: on ? '#FFFFFF' : colors.text, border: '2px solid ' + (on ? '#1E293B' : colors.separator), minHeight: 64, flex: 'none', width: 'calc((100% - 16px) / 3)', boxSizing: 'border-box', padding: 8, borderRadius: 12, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
						>
							<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
								<span aria-hidden="true" style={swatch(key, 14)} />
								<span style={{ fontSize: 19, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
							</span>
							<span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.15 }}>{n > 1 ? TOUR_STATES[key].plural : TOUR_STATES[key].label}</span>
						</button>
					)
				})}
			</div>
			</div>
			)}
		</div>
	)

	let body = null
	if (!pitches && loadError)
		body = <Notice icon={<NoResultIcon />} title="Impossible de charger les emplacements" hint="Vérifiez la connexion, puis réessayez." action={<div style={{ marginLeft: -12 }}><LinkButton onClick={onRetry}>Réessayer</LinkButton></div>} />
	else if (!pitches)
		body = <LoadingText>Chargement des emplacements…</LoadingText>
	else if (scope.length === 0)
	{
		body = (
			<div role="status" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '32px 20px', borderRadius: 12, background: '#F1F5F9', textAlign: 'center' }}>
				<NoResultIcon />
				<p style={{ fontSize: 17, fontWeight: 700 }}>{searching ? 'Aucun emplacement pour « ' + query.trim() + ' »' : 'Aucun emplacement dans ce filtre'}</p>
				<p style={{ fontSize: 15, color: colors.textSecondary }}>Vérifiez le numéro ou retirez un filtre.</p>
			</div>
		)
	}
	else if (view === 'plan')
	{
		// Toutes les zones : un intertitre par zone ; une zone ou une recherche : un seul bloc
		let groups = zones.map(group => ({ ...group, shown: group.pitches.filter(pitch => inScope.has(pitch.location)), title: true }))
		if (searching)
			groups = [{ zone: 'search', label: '', pitches: scope, shown: scope, title: false }]
		else if (zone !== 'all')
			groups = groups.filter(group => group.zone === zone)
		groups = groups.filter(group => group.shown.length > 0)
		body = groups.map(group => {
			const progress = zoneProgress(group.pitches, tour)
			return (
				<section key={group.zone || '-'} style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 16 }}>
					{group.title && (
						<h2 style={{ fontSize: 17, fontWeight: 800, display: 'flex', alignItems: 'baseline', gap: 8, color: colors.text }}>
							{group.label}
							<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{progress.done + ' / ' + progress.total + ' faits'}</span>
						</h2>
					)}
					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
						{group.shown.map(pitch => <Tile key={pitch.location} pitch={pitch} state={pitchState(tour, pitch.location)} both={hasInventoryAndIntervention(tour, pitch.location)} commented={hasComment(tour, pitch.location)} onPick={() => onPick(pitch)} />)}
					</div>
				</section>
			)
		})
	}
	else
	{
		const ordered = [].concat(...zones.map(group => group.pitches)).filter(pitch => inScope.has(pitch.location))
		const sub = { todo: 'Ordre de passage : par zone, puis par numéro', done: 'Par zone, puis par numéro', intervention: 'Par zone, puis par numéro', inventory: 'Par zone, puis par numéro' }
		body = (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
				{STATE_ORDER.map(key => {
					// Sans filtre, un bloc par état (chaque emplacement une seule fois) ; avec un filtre, un seul bloc, celui du filtre
					const rows = filter ? (key === filter ? ordered : []) : ordered.filter(pitch => pitchState(tour, pitch.location) === key)
					if (rows.length === 0)
						return null
					return (
						<section key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
							<h2 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 20, fontWeight: 800, color: colors.text }}>
								<span aria-hidden="true" style={swatch(key, 16)} />
								{TOUR_STATES[key].label}
								<span style={{ fontSize: 17, fontWeight: 700, color: colors.textSecondary }}>{'(' + rows.length + ')'}</span>
							</h2>
							<p style={{ fontSize: 15, color: colors.textSecondary, paddingBottom: 8 }}>{sub[key]}</p>
							<div style={{ display: 'flex', flexDirection: 'column' }}>
								{rows.map(pitch => <Row key={pitch.location} pitch={pitch} state={key} reason={pitchReason(tour, pitch.location, now)} onPick={() => onPick(pitch)} />)}
							</div>
						</section>
					)
				})}
			</div>
		)
	}

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: colors.surface, marginTop: 'calc(-1 * max(12px, env(safe-area-inset-top)))' }}>
			{header}
			<div style={{ flex: 1, padding: '16px 20px 32px', paddingBottom: 'max(32px, env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 16 }}>
				{body}
			</div>
		</div>
	)
}
